import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import type { NotificationEvent } from "@/server-types";
import dayjs from "dayjs";
import "dayjs/locale/bs";
import "dayjs/locale/de";
import "dayjs/locale/en";
import "dayjs/locale/es";
import "dayjs/locale/fr";
import "dayjs/locale/hr";
import "dayjs/locale/it";
import "dayjs/locale/sl";
import "dayjs/locale/sr";
// Source-of-truth catalog lives on the server (runtime-side) so both
// the appointment renderer and the subscription renderer import from
// the same place. Client reaches into `server/src` via relative path —
// the file has no runtime server deps (no Prisma, no env), so Vite
// bundles it without issues.
import {
  getTagsForEvent as getSharedTags,
  type NotificationEventName,
  type TagCategory,
  type TagKey,
} from "../../../../../server/src/Controllers/notification-setting/helpers/notification-tags";

// Merge-tag placeholder shape. Keys must start with a letter and contain
// only alphanumerics — matches the `\w+` loose pattern we used before
// but rejects digit-only strings like `{123}` that can't possibly be
// valid catalog keys. Factory because the `g` flag makes the instance
// stateful (`lastIndex`), so each call site gets its own regex.
export function mergeTagPattern(): RegExp {
  return /\{([a-zA-Z][a-zA-Z0-9]*)\}/g;
}

// Presentation-only labels. The *which tags apply to which event* decision
// lives server-side in `shared/constants/notification-tags.ts`; labels
// stay here as Lingui message descriptors so they translate at render
// without a round-trip to the server or a build step on the shared package.
const TAG_LABEL: Record<TagKey, MessageDescriptor> = {
  clientName: msg`Ime stranke`,
  clientLastName: msg`Priimek stranke`,
  customerName: msg`Ime stranke`,
  customerLastName: msg`Priimek stranke`,
  businessName: msg`Ime podjetja`,
  day: msg`Datum`,
  dayName: msg`Ime dneva`,
  year: msg`Leto`,
  time: msg`Ura`,
  serviceName: msg`Storitev`,
  appointmentPrice: msg`Cena storitve`,
  employeeName: msg`Ime zaposlenega`,
  employeePhone: msg`Telefon zaposlenega`,
  tag: msg`Oznaka storitve`,
  rescheduleLink: msg`Povezava za prestavitev`,
  rescheduleFromDateTime: msg`Čas prejšnjega termina`,
  locationName: msg`Ime lokacije`,
  locationAddress: msg`Naslov lokacije`,
  locationPhone: msg`Telefon lokacije`,
  subscriptionName: msg`Ime paketa`,
  subscriptionPrice: msg`Cena paketa`,
  subscriptionStatus: msg`Status paketa`,
  creditsRemaining: msg`Preostali dobropisi v paketu`,
  visitsRemaining: msg`Preostali obiski v paketu`,
  periodStart: msg`Začetek obdobja paketa`,
  periodEnd: msg`Konec obdobja paketa`,
  amountDue: msg`Znesek za plačilo paketa`,
  dueDate: msg`Rok plačila paketa`,
  daysOverdue: msg`Dnevi zamude s plačilom paketa`,
  cancellationDate: msg`Datum samodejnega preklica paketa`,
  unpaidSince: msg`Paket neplačan od`,
  documentNumber: msg`Številka dokumenta obdobja`,
  previousPeriodStart: msg`Začetek prejšnjega obdobja`,
  previousPeriodEnd: msg`Konec prejšnjega obdobja`,
  currentPeriodStart: msg`Začetek trenutnega obdobja`,
  currentPeriodEnd: msg`Konec trenutnega obdobja`,
  nextPeriodStart: msg`Začetek naslednjega obdobja`,
  nextPeriodEnd: msg`Konec naslednjega obdobja`,
  nextPeriodAmount: msg`Znesek naslednjega obdobja paketa`,
  freezeStart: msg`Začetek zamrznitve paketa`,
  freezeEnd: msg`Konec zamrznitve paketa`,
};

const CATEGORY_ORDER: readonly TagCategory[] = [
  "customer",
  "appointment",
  "location",
  "subscription",
  "business",
] as const;

const CATEGORY_LABEL: Record<TagCategory, MessageDescriptor> = {
  customer: msg`Stranka`,
  appointment: msg`Termin`,
  location: msg`Lokacija`,
  subscription: msg`Paket`,
  business: msg`Podjetje`,
};

export type MergeTag = {
  tag: string;
  /** Lingui message descriptor; resolve with `i18n._(label)` at render. */
  label: MessageDescriptor;
  /** TagKey from the shared catalog; consumers use this to look up the
   *  worst-case length via `worstCaseLengthForTag`. */
  key: TagKey;
};

export type MergeTagGroup = {
  /** Lingui message descriptor; resolve with `i18n._(category)` at render. */
  category: MessageDescriptor;
  items: MergeTag[];
};

export function mergeTagGroupsForEvent(
  event: NotificationEvent,
): MergeTagGroup[] {
  const defs = getSharedTags(event as NotificationEventName);
  const byCategory = new Map<TagCategory, MergeTag[]>();
  for (const def of defs) {
    const bucket = byCategory.get(def.category) ?? [];
    bucket.push({
      tag: def.placeholder,
      label: TAG_LABEL[def.key],
      key: def.key,
    });
    byCategory.set(def.category, bucket);
  }
  return CATEGORY_ORDER.flatMap((c) =>
    byCategory.has(c)
      ? [
          {
            category: CATEGORY_LABEL[c],
            items: byCategory.get(c)!,
          },
        ]
      : [],
  );
}

// ─────────────────────────────────────────────────────────
//  Preview values (visual render)
// ─────────────────────────────────────────────────────────
//
//  Plausible sample values used when rendering phone/email previews so
//  operators see roughly what a dispatched message will look like.
//  Different from char-usage computation below: previews use one readable
//  sample per tag; char usage uses the tenant's *longest realistic* value
//  so the SMS-parts counter reflects worst-case message length.

// One anchored date behind {day}/{dayName}/{year}/{time} so the preview
// never shows a weekday that contradicts its own date.
// Pinned to `en` so an unknown/unloaded locale falls back to English
// rather than to whatever locale dayjs happens to be set to globally.
const PREVIEW_DATE = dayjs("2026-04-13T10:00:00").locale("en");

// The dispatcher renders `{dayName}` with `dayjs(...).locale(language)`
// and no capitalization, so the preview must do the same or it stops
// matching the message that actually goes out (DEV-9344).
function previewDayName(locale?: string): string {
  return PREVIEW_DATE.locale(locale ?? "en").format("dddd");
}

// Default sample values for tags whose data we can't pull from the
// tenant (customer/employee identities, fixed-format dates). Real tenant
// fields get layered on top by `buildPreviewData`.
const PREVIEW_DEFAULTS: Record<TagKey, string> = {
  clientName: "Ana",
  clientLastName: "Novak",
  customerName: "Ana",
  customerLastName: "Novak",
  businessName: "Studio Lime",
  day: PREVIEW_DATE.format("DD. MM."),
  dayName: previewDayName(),
  year: PREVIEW_DATE.format("YYYY"),
  time: PREVIEW_DATE.format("HH:mm"),
  serviceName: "Haircut & Style",
  appointmentPrice: "25,00 €",
  employeeName: "Maja Kovač",
  employeePhone: "+386 40 123 456",
  tag: "Regular",
  // Short-URL form used by the outbound notification pipeline (mirrors
  // the server's link-shortener output). 23 chars, matches what gets
  // counted against the SMS budget at send time.
  rescheduleLink: "https://eej.at/Iyjk4RYt",
  rescheduleFromDateTime: "12. 04. 2026. 09:00",
  locationName: "Studio Lime — Center",
  locationAddress: "Ulica primer 12, 1000 Ljubljana",
  locationPhone: "+386 1 234 5678",
  subscriptionName: "Gold Package",
  subscriptionPrice: "49,00 €",
  subscriptionStatus: "Aktivno",
  creditsRemaining: "2",
  visitsRemaining: "8",
  periodStart: "2026-04-01",
  periodEnd: "2026-05-01",
  amountDue: "49,00 €",
  dueDate: "2026-04-01",
  daysOverdue: "3",
  cancellationDate: "2026-04-15",
  unpaidSince: "2026-04-01",
  documentNumber: "2026-00042",
  previousPeriodStart: "2026-03-01",
  previousPeriodEnd: "2026-04-01",
  currentPeriodStart: "2026-04-01",
  currentPeriodEnd: "2026-05-01",
  nextPeriodStart: "2026-05-01",
  nextPeriodEnd: "2026-06-01",
  nextPeriodAmount: "49,00 €",
  freezeStart: "2026-04-15",
  freezeEnd: "2026-04-22",
};

export type PreviewData = Record<TagKey, string>;

/**
 * Default preview values — used before tenant data is loaded or as a
 * fallback for tags whose data we can't fetch. Consumers that have the
 * tenant context should call `buildPreviewData` to get real values for
 * businessName / location / first-service / first-subscription etc.
 */
export const PREVIEW_DATA: PreviewData = PREVIEW_DEFAULTS;

/**
 * Overlays real tenant values onto the default preview data. Called
 * from the content editor drawer, which has already fetched services,
 * subscriptions, locations, and the authenticated business name.
 *
 * Only tags whose data we actually have are overlaid; everything else
 * keeps its default. The caller doesn't have to provide partial data
 * explicitly — any missing field falls through to the default.
 *
 * `locale` is the language the operator is currently editing (DEV-8284).
 * Localized service / subscription / tag names resolve to that locale so
 * the preview matches the message language instead of whichever locale
 * happens to come first in the data (previously always English).
 */
export function buildPreviewData(
  source: CharUsageSource,
  locale?: string,
): PreviewData {
  const out = { ...PREVIEW_DEFAULTS };

  out.dayName = previewDayName(locale);

  if (source.businessName && source.businessName.trim()) {
    out.businessName = source.businessName;
  }

  const firstLocation = source.locations?.[0];
  if (firstLocation?.label) out.locationName = firstLocation.label;
  if (firstLocation?.address) out.locationAddress = firstLocation.address;
  if (firstLocation?.gsm) out.locationPhone = firstLocation.gsm;

  const firstServiceName = pickFirstNonEmpty(
    (source.services ?? []).map(
      (s) => pickLocalizedValue(s.nameLocalized, (n) => n.name, locale) ?? "",
    ),
  );
  if (firstServiceName) out.serviceName = firstServiceName;

  const firstSubscriptionName = pickFirstNonEmpty(
    (source.subscriptions ?? []).map(
      (s) => pickLocalizedValue(s.nameLocalized, (n) => n.value, locale) ?? "",
    ),
  );
  if (firstSubscriptionName) out.subscriptionName = firstSubscriptionName;

  const firstTagName = pickFirstNonEmpty(
    (source.serviceTags ?? []).map(
      (t) =>
        pickLocalizedValue(t.TagNameLocalized, (n) => n.name, locale) ?? "",
    ),
  );
  if (firstTagName) out.tag = firstTagName;

  return out;
}

function pickFirstNonEmpty(values: string[]): string | null {
  for (const v of values) {
    if (v && v.trim()) return v;
  }
  return null;
}

// Resolve a localized value to the editing locale, falling back to the
// first non-empty translation when that locale has no entry (or none was
// requested). Keeps the preview from silently showing a different
// language than the one being edited (DEV-8284).
function pickLocalizedValue<T extends { language?: string | null }>(
  entries: ReadonlyArray<T> | undefined,
  getValue: (entry: T) => string | null | undefined,
  locale: string | undefined,
): string | null {
  if (!entries || entries.length === 0) return null;
  if (locale) {
    const match = entries.find((e) => e.language === locale);
    const value = match ? getValue(match) : null;
    if (value && value.trim()) return value;
  }
  return pickFirstNonEmpty(entries.map((e) => getValue(e) ?? ""));
}

export function renderMergeTagsPreview(
  text: string,
  data: PreviewData = PREVIEW_DEFAULTS,
): string {
  if (!text) return "";
  return text.replace(mergeTagPattern(), (match, key: string) => {
    const expansion = (data as Record<string, string | undefined>)[key];
    return expansion !== undefined ? expansion : match;
  });
}

export type MergeTagSegment =
  | { kind: "text"; value: string }
  | { kind: "tag"; value: string };

// Split a string into alternating plain-text and expanded-tag segments so
// callers can render expanded tags with a visual cue (dotted underline)
// while leaving the rest as-is. Unknown tags fall through as plain text
// wrapped in their original `{braces}`.
export function splitMergeTagSegments(
  text: string,
  data: PreviewData = PREVIEW_DEFAULTS,
): MergeTagSegment[] {
  if (!text) return [];
  const out: MergeTagSegment[] = [];
  const regex = mergeTagPattern();
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      out.push({ kind: "text", value: text.slice(lastIndex, match.index) });
    }
    const rawKey = match[1];
    const expansion = (data as Record<string, string | undefined>)[rawKey];
    if (expansion !== undefined) {
      out.push({ kind: "tag", value: expansion });
    } else {
      out.push({ kind: "text", value: `{${rawKey}}` });
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    out.push({ kind: "text", value: text.slice(lastIndex) });
  }
  return out;
}

// ─────────────────────────────────────────────────────────
//  Worst-case char usage (SMS-parts counter)
// ─────────────────────────────────────────────────────────
//
//  At send time a tag expands to the actual customer/appointment/service
//  value. For SMS-parts counting, using a single preview value
//  understates the worst case — a template that fits in 1 SMS with a
//  short service name might blow through 3 parts for a long one.
//
//  `CharUsageSource` carries the tenant data needed to compute the
//  longest plausible expansion per tag: longest service name across
//  locales, longest location label, etc. Unknown dimensions fall back
//  to a safe default that errs slightly long so the counter doesn't
//  under-report.

export type CharUsageSource = {
  businessName?: string;
  services?: ReadonlyArray<{
    serviceId?: number;
    nameLocalized?: ReadonlyArray<{
      name?: string | null;
      language?: string | null;
    }>;
  }>;
  subscriptions?: ReadonlyArray<{
    nameLocalized?: ReadonlyArray<{
      value?: string | null;
      language?: string | null;
    }>;
  }>;
  locations?: ReadonlyArray<{
    locationId?: number;
    label?: string | null;
    address?: string | null;
    gsm?: string | null;
  }>;
  serviceTags?: ReadonlyArray<{
    TagNameLocalized?: ReadonlyArray<{
      name?: string | null;
      language?: string | null;
    }>;
  }>;
};

/**
 * Narrows a CharUsageSource to only the services / locations a variant
 * actually targets, so previews and the SMS-parts counter use the
 * variant's own selections instead of the account-wide first row. Pass
 * `allServices`/`allLocations` to short-circuit filtering when the
 * variant covers everything.
 */
export function scopeCharUsageSourceToVariant(
  source: CharUsageSource,
  variant: {
    allServices?: boolean;
    services?: ReadonlyArray<{ serviceId: number }>;
    allLocations?: boolean;
    locations?: ReadonlyArray<{ locationId: number }>;
  },
): CharUsageSource {
  const serviceIds = variant.allServices
    ? null
    : new Set((variant.services ?? []).map((s) => s.serviceId));
  const locationIds = variant.allLocations
    ? null
    : new Set((variant.locations ?? []).map((l) => l.locationId));

  const services =
    serviceIds && source.services
      ? source.services.filter((s) =>
          s.serviceId != null ? serviceIds.has(s.serviceId) : false,
        )
      : source.services;

  const locations =
    locationIds && source.locations
      ? source.locations.filter((l) =>
          l.locationId != null ? locationIds.has(l.locationId) : false,
        )
      : source.locations;

  // Narrowing to an empty list (variant selects services/locations that
  // are not in the loaded reference data yet) would force the preview
  // back to PREVIEW_DEFAULTS, which is exactly the bug being fixed.
  // Fall back to the unfiltered list in that race so the operator never
  // sees stock copy.
  return {
    ...source,
    services: services && services.length > 0 ? services : source.services,
    locations: locations && locations.length > 0 ? locations : source.locations,
  };
}

/**
 * Preview data for one variant: scopes the account-wide tenant source to
 * the variant's own service / location selection before overlaying it
 * onto the tag defaults. Every preview surface derives its sample through
 * this single helper so the drawer and the channel snippet can't diverge
 * on which service the sample appointment shows (DEV-10862).
 */
export function buildVariantPreviewData(
  source: CharUsageSource,
  variant: Parameters<typeof scopeCharUsageSourceToVariant>[1],
  locale?: string,
): PreviewData {
  return buildPreviewData(
    scopeCharUsageSourceToVariant(source, variant),
    locale,
  );
}

// Defaults used when the tenant data isn't loaded yet (or when the tag
// expansion isn't derivable from fetchable data — e.g. customer name).
// Biased slightly long: a ~25 char customer name + surname covers most
// realistic cases without massively over-reporting SMS parts.
const DEFAULT_LENGTH: Record<TagKey, number> = {
  clientName: 15,
  clientLastName: 15,
  customerName: 15,
  customerLastName: 15,
  businessName: 20,
  day: 7, // "DD. MM."
  dayName: 11, // "Wednesday" / "Donnerstag" / "ponedjeljak"
  year: 4,
  time: 5, // "HH:mm"
  serviceName: 25,
  appointmentPrice: 14, // "25.000,00 RSD"
  employeeName: 25, // "Firstname Lastname"
  employeePhone: 16, // "+386 40 123 456"
  tag: 15,
  rescheduleLink: 23, // "https://eej.at/Iyjk4RYt"
  rescheduleFromDateTime: 19, // "DD. MM. YYYY. HH:mm"
  locationName: 25,
  locationAddress: 40,
  locationPhone: 16,
  subscriptionName: 25,
  subscriptionPrice: 12, // "1.234,00 €"
  subscriptionStatus: 12, // longest localized status label
  creditsRemaining: 3,
  visitsRemaining: 3,
  periodStart: 10, // "YYYY-MM-DD"
  periodEnd: 10, // "YYYY-MM-DD"
  amountDue: 12, // "1.234,00 €"
  dueDate: 10, // "YYYY-MM-DD"
  daysOverdue: 3,
  cancellationDate: 10, // "YYYY-MM-DD"
  unpaidSince: 10, // "YYYY-MM-DD"
  documentNumber: 16, // tax-register numbering varies per organization
  previousPeriodStart: 10,
  previousPeriodEnd: 10,
  currentPeriodStart: 10,
  currentPeriodEnd: 10,
  nextPeriodStart: 10,
  nextPeriodEnd: 10,
  nextPeriodAmount: 12,
  freezeStart: 10,
  freezeEnd: 10,
};

function longestNonEmpty(values: Array<string | null | undefined>): number {
  let max = 0;
  for (const v of values) {
    if (v && v.length > max) max = v.length;
  }
  return max;
}

/**
 * Public wrapper around the per-tag worst-case length used by both the
 * total character counter and the per-chip length hint (DEV-8044).
 * Single source of truth so a hint of "Customer name (15)" matches the
 * length the counter actually reserved for that tag.
 */
export function worstCaseLengthForTag(
  key: TagKey,
  source: CharUsageSource,
): number {
  return worstCaseLengthFor(key, source);
}

function worstCaseLengthFor(key: TagKey, source: CharUsageSource): number {
  switch (key) {
    case "businessName":
      return source.businessName?.length ?? DEFAULT_LENGTH.businessName;
    case "serviceName": {
      const lengths = (source.services ?? []).flatMap((s) =>
        (s.nameLocalized ?? []).map((n) => n.name ?? ""),
      );
      return Math.max(longestNonEmpty(lengths), DEFAULT_LENGTH.serviceName);
    }
    case "subscriptionName": {
      const lengths = (source.subscriptions ?? []).flatMap((s) =>
        (s.nameLocalized ?? []).map((n) => n.value ?? ""),
      );
      return Math.max(
        longestNonEmpty(lengths),
        DEFAULT_LENGTH.subscriptionName,
      );
    }
    case "locationName":
      return Math.max(
        longestNonEmpty((source.locations ?? []).map((l) => l.label ?? "")),
        DEFAULT_LENGTH.locationName,
      );
    case "locationAddress":
      return Math.max(
        longestNonEmpty((source.locations ?? []).map((l) => l.address ?? "")),
        DEFAULT_LENGTH.locationAddress,
      );
    case "locationPhone":
      return Math.max(
        longestNonEmpty((source.locations ?? []).map((l) => l.gsm ?? "")),
        DEFAULT_LENGTH.locationPhone,
      );
    case "tag":
      return Math.max(
        longestNonEmpty(
          (source.serviceTags ?? []).flatMap((t) =>
            (t.TagNameLocalized ?? []).map((n) => n.name ?? ""),
          ),
        ),
        DEFAULT_LENGTH.tag,
      );
    default:
      return DEFAULT_LENGTH[key];
  }
}

/**
 * Expand a template into a string of its worst-case length. Structural
 * characters (everything outside `{braces}`) count as themselves; each
 * tag expands to an X-padded placeholder of the longest realistic value
 * for that tag given the loaded tenant data. Pass the result to
 * `countSmsParts` to get a conservative SMS-parts estimate.
 */
export function expandForCharUsage(
  template: string,
  source: CharUsageSource,
): string {
  if (!template) return "";
  return template.replace(mergeTagPattern(), (match, rawKey: string) => {
    const key = rawKey as TagKey;
    if (!(key in DEFAULT_LENGTH)) return match; // unknown tag — leave as-is
    return "X".repeat(worstCaseLengthFor(key, source));
  });
}
